import React, { useState, useEffect } from 'react';
import { Lock, Package, DollarSign, Music, Calendar, Mail, CreditCard, Eye, EyeOff, RefreshCw, FileText, Plus, Edit, Trash2, X, Save, Gift, Users, Download, Share2, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social icons for admin display
const XIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

export const Admin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [blogPosts, setBlogPosts] = useState([]);
    const [leadSignups, setLeadSignups] = useState([]);
    const [newsletterSubs, setNewsletterSubs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('orders');
    
    // Blog editor state
    const [showBlogEditor, setShowBlogEditor] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [blogForm, setBlogForm] = useState({ 
        title: '', 
        excerpt: '', 
        content: '', 
        published: true,
        seo_slug: '',
        seo_title: '',
        seo_description: ''
    });
    
    // Share tracking state
    const [shareStats, setShareStats] = useState(null);
    const [shareStartDate, setShareStartDate] = useState('');
    const [shareEndDate, setShareEndDate] = useState('');
    
    // Date filter states for other tabs
    const [ordersStartDate, setOrdersStartDate] = useState('');
    const [ordersEndDate, setOrdersEndDate] = useState('');
    const [downloadsStartDate, setDownloadsStartDate] = useState('');
    const [downloadsEndDate, setDownloadsEndDate] = useState('');
    const [newsletterStartDate, setNewsletterStartDate] = useState('');
    const [newsletterEndDate, setNewsletterEndDate] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        
        try {
            await axios.post(`${API}/admin/verify`, { password });
            setIsAuthenticated(true);
            sessionStorage.setItem('adminAuth', password);
            fetchData(password);
        } catch (err) {
            setError('Invalid password');
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async (pwd) => {
        setLoading(true);
        try {
            const [ordersRes, statsRes, blogRes, leadsRes, newsletterRes, sharesRes] = await Promise.all([
                axios.get(`${API}/admin/orders?password=${encodeURIComponent(pwd)}`),
                axios.get(`${API}/admin/stats?password=${encodeURIComponent(pwd)}`),
                axios.get(`${API}/blog/posts?published_only=false`),
                axios.get(`${API}/admin/lead-signups?password=${encodeURIComponent(pwd)}`),
                axios.get(`${API}/admin/newsletter?password=${encodeURIComponent(pwd)}`),
                axios.get(`${API}/admin/shares?password=${encodeURIComponent(pwd)}`)
            ]);
            setOrders(ordersRes.data);
            setStats(statsRes.data);
            setBlogPosts(blogRes.data);
            setLeadSignups(leadsRes.data);
            setNewsletterSubs(newsletterRes.data);
            setShareStats(sharesRes.data);
        } catch (err) {
            setError('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch shares with date filter
    const fetchSharesWithFilter = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        try {
            let url = `${API}/admin/shares?password=${encodeURIComponent(pwd)}`;
            if (shareStartDate) url += `&start_date=${shareStartDate}`;
            if (shareEndDate) url += `&end_date=${shareEndDate}`;
            
            const res = await axios.get(url);
            setShareStats(res.data);
        } catch (err) {
            console.error('Failed to fetch shares:', err);
        }
    };
    
    // Delete shares (with optional date filter)
    const deleteShares = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        const filterText = (shareStartDate || shareEndDate) 
            ? `from ${shareStartDate || 'beginning'} to ${shareEndDate || 'now'}`
            : 'ALL shares';
        
        if (!window.confirm(`Are you sure you want to delete ${filterText}? This cannot be undone.`)) {
            return;
        }
        
        try {
            let url = `${API}/admin/shares?password=${encodeURIComponent(pwd)}`;
            if (shareStartDate) url += `&start_date=${shareStartDate}`;
            if (shareEndDate) url += `&end_date=${shareEndDate}`;
            
            const res = await axios.delete(url);
            alert(`Deleted ${res.data.deleted_count} share records.`);
            
            // Refresh the data
            fetchSharesWithFilter();
        } catch (err) {
            console.error('Failed to delete shares:', err);
            alert('Failed to delete shares. Please try again.');
        }
    };
    
    // Filter and delete functions for Orders
    const fetchOrdersWithFilter = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        try {
            let url = `${API}/admin/orders?password=${encodeURIComponent(pwd)}`;
            if (ordersStartDate) url += `&start_date=${ordersStartDate}`;
            if (ordersEndDate) url += `&end_date=${ordersEndDate}`;
            
            const res = await axios.get(url);
            setOrders(res.data);
        } catch (err) {
            console.error('Failed to fetch orders:', err);
        }
    };
    
    const deleteOrders = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        const filterText = (ordersStartDate || ordersEndDate) 
            ? `from ${ordersStartDate || 'beginning'} to ${ordersEndDate || 'now'}`
            : 'ALL orders';
        
        if (!window.confirm(`Are you sure you want to delete ${filterText}? This cannot be undone.`)) {
            return;
        }
        
        try {
            let url = `${API}/admin/orders?password=${encodeURIComponent(pwd)}`;
            if (ordersStartDate) url += `&start_date=${ordersStartDate}`;
            if (ordersEndDate) url += `&end_date=${ordersEndDate}`;
            
            const res = await axios.delete(url);
            alert(`Deleted ${res.data.deleted_count} orders.`);
            fetchOrdersWithFilter();
        } catch (err) {
            console.error('Failed to delete orders:', err);
            alert('Failed to delete orders. Please try again.');
        }
    };
    
    // Filter and delete functions for Downloads (Lead Signups)
    const fetchDownloadsWithFilter = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        try {
            let url = `${API}/admin/lead-signups?password=${encodeURIComponent(pwd)}`;
            if (downloadsStartDate) url += `&start_date=${downloadsStartDate}`;
            if (downloadsEndDate) url += `&end_date=${downloadsEndDate}`;
            
            const res = await axios.get(url);
            setLeadSignups(res.data);
        } catch (err) {
            console.error('Failed to fetch downloads:', err);
        }
    };
    
    const deleteDownloads = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        const filterText = (downloadsStartDate || downloadsEndDate) 
            ? `from ${downloadsStartDate || 'beginning'} to ${downloadsEndDate || 'now'}`
            : 'ALL downloads';
        
        if (!window.confirm(`Are you sure you want to delete ${filterText}? This cannot be undone.`)) {
            return;
        }
        
        try {
            let url = `${API}/admin/lead-signups?password=${encodeURIComponent(pwd)}`;
            if (downloadsStartDate) url += `&start_date=${downloadsStartDate}`;
            if (downloadsEndDate) url += `&end_date=${downloadsEndDate}`;
            
            const res = await axios.delete(url);
            alert(`Deleted ${res.data.deleted_count} downloads.`);
            fetchDownloadsWithFilter();
        } catch (err) {
            console.error('Failed to delete downloads:', err);
            alert('Failed to delete downloads. Please try again.');
        }
    };
    
    // Filter and delete functions for Newsletter
    const fetchNewsletterWithFilter = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        try {
            let url = `${API}/admin/newsletter?password=${encodeURIComponent(pwd)}`;
            if (newsletterStartDate) url += `&start_date=${newsletterStartDate}`;
            if (newsletterEndDate) url += `&end_date=${newsletterEndDate}`;
            
            const res = await axios.get(url);
            setNewsletterSubs(res.data);
        } catch (err) {
            console.error('Failed to fetch newsletter:', err);
        }
    };
    
    const deleteNewsletter = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (!pwd) return;
        
        const filterText = (newsletterStartDate || newsletterEndDate) 
            ? `from ${newsletterStartDate || 'beginning'} to ${newsletterEndDate || 'now'}`
            : 'ALL newsletter subscribers';
        
        if (!window.confirm(`Are you sure you want to delete ${filterText}? This cannot be undone.`)) {
            return;
        }
        
        try {
            let url = `${API}/admin/newsletter?password=${encodeURIComponent(pwd)}`;
            if (newsletterStartDate) url += `&start_date=${newsletterStartDate}`;
            if (newsletterEndDate) url += `&end_date=${newsletterEndDate}`;
            
            const res = await axios.delete(url);
            alert(`Deleted ${res.data.deleted_count} subscribers.`);
            fetchNewsletterWithFilter();
        } catch (err) {
            console.error('Failed to delete newsletter:', err);
            alert('Failed to delete newsletter subscribers. Please try again.');
        }
    };

    const handleRefresh = () => {
        const pwd = sessionStorage.getItem('adminAuth');
        if (pwd) fetchData(pwd);
    };

    useEffect(() => {
        const savedAuth = sessionStorage.getItem('adminAuth');
        if (savedAuth) {
            setPassword(savedAuth);
            setIsAuthenticated(true);
            fetchData(savedAuth);
        }
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Blog functions
    const openNewPost = () => {
        setEditingPost(null);
        setBlogForm({ 
            title: '', 
            excerpt: '', 
            content: '', 
            published: true,
            seo_slug: '',
            seo_title: '',
            seo_description: ''
        });
        setShowBlogEditor(true);
    };

    const openEditPost = (post) => {
        setEditingPost(post);
        setBlogForm({
            title: post.title,
            excerpt: post.excerpt,
            content: post.content,
            published: post.published,
            seo_slug: post.seo_slug || '',
            seo_title: post.seo_title || '',
            seo_description: post.seo_description || ''
        });
        setShowBlogEditor(true);
    };

    const closeBlogEditor = () => {
        setShowBlogEditor(false);
        setEditingPost(null);
        setBlogForm({ 
            title: '', 
            excerpt: '', 
            content: '', 
            published: true,
            seo_slug: '',
            seo_title: '',
            seo_description: ''
        });
    };

    const saveBlogPost = async () => {
        const pwd = sessionStorage.getItem('adminAuth');
        try {
            if (editingPost) {
                await axios.put(`${API}/admin/blog/posts/${editingPost.slug}?password=${encodeURIComponent(pwd)}`, blogForm);
            } else {
                await axios.post(`${API}/admin/blog/posts?password=${encodeURIComponent(pwd)}`, blogForm);
            }
            closeBlogEditor();
            fetchData(pwd);
        } catch (err) {
            setError('Failed to save blog post');
        }
    };

    const deleteBlogPost = async (slug) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        const pwd = sessionStorage.getItem('adminAuth');
        try {
            await axios.delete(`${API}/admin/blog/posts/${slug}?password=${encodeURIComponent(pwd)}`);
            fetchData(pwd);
        } catch (err) {
            setError('Failed to delete blog post');
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24 flex items-center justify-center">
                <div className="bg-white rounded-2xl shadow-soft p-8 max-w-md w-full mx-4">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-copper-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-copper-500" />
                        </div>
                        <h1 className="font-heading text-2xl text-nature-900">Admin Access</h1>
                        <p className="text-nature-800/60 font-body text-sm mt-2">Enter password to continue</p>
                    </div>
                    
                    <form onSubmit={handleLogin}>
                        <div className="relative mb-4">
                            <Input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="pr-10"
                                data-testid="admin-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-nature-800/50 hover:text-nature-800"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        
                        {error && (
                            <p className="text-red-500 text-sm mb-4 text-center">{error}</p>
                        )}
                        
                        <Button 
                            type="submit" 
                            className="w-full bg-copper-500 hover:bg-copper-600"
                            disabled={loading}
                            data-testid="admin-login-button"
                        >
                            {loading ? 'Verifying...' : 'Access Dashboard'}
                        </Button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-nature-50 pt-24 pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading text-3xl text-nature-900">Admin Dashboard</h1>
                        <p className="text-nature-800/60 font-body">Hypnosis Mastery Series</p>
                    </div>
                    <Button 
                        onClick={handleRefresh} 
                        variant="outline" 
                        className="rounded-full"
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                        <div className="bg-white rounded-xl p-6 shadow-soft">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <DollarSign className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-nature-800/60 font-body text-sm">Total Revenue</p>
                                    <p className="font-heading text-2xl text-nature-900">${stats.total_revenue.toFixed(2)}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-xl p-6 shadow-soft">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Package className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-nature-800/60 font-body text-sm">Total Orders</p>
                                    <p className="font-heading text-2xl text-nature-900">{stats.total_orders}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-xl p-6 shadow-soft">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                    <Music className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-nature-800/60 font-body text-sm">Recordings Sold</p>
                                    <p className="font-heading text-2xl text-nature-900">{stats.total_recordings_sold}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-xl p-6 shadow-soft">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-copper-100 rounded-full flex items-center justify-center">
                                    <Gift className="w-6 h-6 text-copper-600" />
                                </div>
                                <div>
                                    <p className="text-nature-800/60 font-body text-sm">Free Downloads</p>
                                    <p className="font-heading text-2xl text-nature-900">{leadSignups.length}</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white rounded-xl p-6 shadow-soft">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                                    <Users className="w-6 h-6 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-nature-800/60 font-body text-sm">Newsletter</p>
                                    <p className="font-heading text-2xl text-nature-900">{newsletterSubs.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 mb-6 flex-wrap">
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                            activeTab === 'orders' 
                                ? 'bg-copper-500 text-white' 
                                : 'bg-white text-nature-800 hover:bg-nature-100'
                        }`}
                    >
                        Orders
                    </button>
                    <button
                        onClick={() => setActiveTab('leads')}
                        className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                            activeTab === 'leads' 
                                ? 'bg-copper-500 text-white' 
                                : 'bg-white text-nature-800 hover:bg-nature-100'
                        }`}
                    >
                        Downloads ({leadSignups.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('newsletter')}
                        className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                            activeTab === 'newsletter' 
                                ? 'bg-copper-500 text-white' 
                                : 'bg-white text-nature-800 hover:bg-nature-100'
                        }`}
                    >
                        Newsletter ({newsletterSubs.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('shares')}
                        className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                            activeTab === 'shares' 
                                ? 'bg-copper-500 text-white' 
                                : 'bg-white text-nature-800 hover:bg-nature-100'
                        }`}
                    >
                        Shares ({shareStats?.total_shares || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('blog')}
                        className={`px-4 py-2 rounded-full font-body text-sm transition-colors ${
                            activeTab === 'blog' 
                                ? 'bg-copper-500 text-white' 
                                : 'bg-white text-nature-800 hover:bg-nature-100'
                        }`}
                    >
                        Blog Posts
                    </button>
                </div>

                {/* Orders Table */}
                {activeTab === 'orders' && (
                    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                        <div className="p-6 border-b border-nature-100">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <h2 className="font-heading text-xl text-nature-900">Recent Orders</h2>
                                
                                {/* Date Filter */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-nature-800/60 font-body">From:</label>
                                        <Input
                                            type="date"
                                            value={ordersStartDate}
                                            onChange={(e) => setOrdersStartDate(e.target.value)}
                                            className="w-36"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-nature-800/60 font-body">To:</label>
                                        <Input
                                            type="date"
                                            value={ordersEndDate}
                                            onChange={(e) => setOrdersEndDate(e.target.value)}
                                            className="w-36"
                                        />
                                    </div>
                                    <Button 
                                        onClick={fetchOrdersWithFilter}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        Filter
                                    </Button>
                                    {(ordersStartDate || ordersEndDate) && (
                                        <Button 
                                            onClick={() => {
                                                setOrdersStartDate('');
                                                setOrdersEndDate('');
                                                const pwd = sessionStorage.getItem('adminAuth');
                                                if (pwd) {
                                                    axios.get(`${API}/admin/orders?password=${encodeURIComponent(pwd)}`)
                                                        .then(res => setOrders(res.data));
                                                }
                                            }}
                                            variant="ghost"
                                            className="rounded-full text-sm"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                    {orders.length > 0 && (
                                        <Button 
                                            onClick={deleteOrders}
                                            variant="outline"
                                            className="rounded-full text-red-500 hover:text-red-600 hover:border-red-300"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete {(ordersStartDate || ordersEndDate) ? 'Filtered' : 'All'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {orders.length === 0 ? (
                            <div className="p-12 text-center">
                                <Package className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                                <p className="text-nature-800/60 font-body">No orders yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-nature-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Customer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Recordings</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Total</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-nature-100">
                                        {orders.map((order, index) => (
                                            <tr key={order.id || index} className="hover:bg-nature-50/50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-nature-800">
                                                        <Calendar className="w-4 h-4 text-nature-800/40" />
                                                        {formatDate(order.created_at)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-nature-800">
                                                        <Mail className="w-4 h-4 text-nature-800/40" />
                                                        {order.email || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-nature-800">
                                                        {order.recording_ids?.length || 0} recording(s)
                                                        {order.download_links && (
                                                            <div className="text-xs text-nature-800/50 mt-1">
                                                                {order.download_links.map(dl => dl.title).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm font-medium text-nature-900">
                                                        <CreditCard className="w-4 h-4 text-nature-800/40" />
                                                        ${order.total?.toFixed(2) || '0.00'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        order.status === 'completed' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : order.status === 'pending'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {order.status || 'unknown'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Lead Magnet Signups */}
                {activeTab === 'leads' && (
                    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                        <div className="p-6 border-b border-nature-100">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                                <h2 className="font-heading text-xl text-nature-900">Free Recording Downloads</h2>
                                <Button 
                                    onClick={() => {
                                        const csv = "Download ID,Recording,Date\n" + leadSignups.map(s => 
                                            `${s.download_id || s.email || '-'},${s.recording || 'Freedom from Insomnia'},${s.signed_up_at}`
                                        ).join('\n');
                                        const blob = new Blob([csv], { type: 'text/csv' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'free-downloads.csv';
                                        a.click();
                                    }}
                                    variant="outline"
                                    className="rounded-full"
                                    disabled={leadSignups.length === 0}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                            
                            {/* Date Filter */}
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-nature-800/60 font-body">From:</label>
                                    <Input
                                        type="date"
                                        value={downloadsStartDate}
                                        onChange={(e) => setDownloadsStartDate(e.target.value)}
                                        className="w-36"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-nature-800/60 font-body">To:</label>
                                    <Input
                                        type="date"
                                        value={downloadsEndDate}
                                        onChange={(e) => setDownloadsEndDate(e.target.value)}
                                        className="w-36"
                                    />
                                </div>
                                <Button 
                                    onClick={fetchDownloadsWithFilter}
                                    variant="outline"
                                    className="rounded-full"
                                >
                                    Filter
                                </Button>
                                {(downloadsStartDate || downloadsEndDate) && (
                                    <Button 
                                        onClick={() => {
                                            setDownloadsStartDate('');
                                            setDownloadsEndDate('');
                                            const pwd = sessionStorage.getItem('adminAuth');
                                            if (pwd) {
                                                axios.get(`${API}/admin/lead-signups?password=${encodeURIComponent(pwd)}`)
                                                    .then(res => setLeadSignups(res.data));
                                            }
                                        }}
                                        variant="ghost"
                                        className="rounded-full text-sm"
                                    >
                                        Clear
                                    </Button>
                                )}
                                {leadSignups.length > 0 && (
                                    <Button 
                                        onClick={deleteDownloads}
                                        variant="outline"
                                        className="rounded-full text-red-500 hover:text-red-600 hover:border-red-300"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete {(downloadsStartDate || downloadsEndDate) ? 'Filtered' : 'All'}
                                    </Button>
                                )}
                            </div>
                        </div>
                        
                        {leadSignups.length === 0 ? (
                            <div className="p-12 text-center">
                                <Gift className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                                <p className="text-nature-800/60 font-body">No free downloads yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-nature-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Download ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Recording</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Date & Time</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-nature-100">
                                        {leadSignups.map((signup, index) => (
                                            <tr key={index} className="hover:bg-nature-50/50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-mono text-nature-800">
                                                        {signup.download_id || signup.email || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-nature-800">
                                                        {signup.recording || 'Freedom from Insomnia'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-nature-800">
                                                        <Calendar className="w-4 h-4 text-nature-800/40" />
                                                        {formatDate(signup.signed_up_at)}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Newsletter Subscribers */}
                {activeTab === 'newsletter' && (
                    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                        <div className="p-6 border-b border-nature-100">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                                <h2 className="font-heading text-xl text-nature-900">Newsletter Subscribers</h2>
                                <Button 
                                    onClick={() => {
                                        const csv = "Email,Source,Date\n" + newsletterSubs.map(s => 
                                            `${s.email},${s.source || 'website'},${s.subscribed_at}`
                                        ).join('\n');
                                        const blob = new Blob([csv], { type: 'text/csv' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'newsletter-subscribers.csv';
                                        a.click();
                                    }}
                                    variant="outline"
                                    className="rounded-full"
                                    disabled={newsletterSubs.length === 0}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Export CSV
                                </Button>
                            </div>
                            
                            {/* Date Filter */}
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-nature-800/60 font-body">From:</label>
                                    <Input
                                        type="date"
                                        value={newsletterStartDate}
                                        onChange={(e) => setNewsletterStartDate(e.target.value)}
                                        className="w-36"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-nature-800/60 font-body">To:</label>
                                    <Input
                                        type="date"
                                        value={newsletterEndDate}
                                        onChange={(e) => setNewsletterEndDate(e.target.value)}
                                        className="w-36"
                                    />
                                </div>
                                <Button 
                                    onClick={fetchNewsletterWithFilter}
                                    variant="outline"
                                    className="rounded-full"
                                >
                                    Filter
                                </Button>
                                {(newsletterStartDate || newsletterEndDate) && (
                                    <Button 
                                        onClick={() => {
                                            setNewsletterStartDate('');
                                            setNewsletterEndDate('');
                                            const pwd = sessionStorage.getItem('adminAuth');
                                            if (pwd) {
                                                axios.get(`${API}/admin/newsletter?password=${encodeURIComponent(pwd)}`)
                                                    .then(res => setNewsletterSubs(res.data));
                                            }
                                        }}
                                        variant="ghost"
                                        className="rounded-full text-sm"
                                    >
                                        Clear
                                    </Button>
                                )}
                                {newsletterSubs.length > 0 && (
                                    <Button 
                                        onClick={deleteNewsletter}
                                        variant="outline"
                                        className="rounded-full text-red-500 hover:text-red-600 hover:border-red-300"
                                    >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete {(newsletterStartDate || newsletterEndDate) ? 'Filtered' : 'All'}
                                    </Button>
                                )}
                            </div>
                        </div>
                        
                        {newsletterSubs.length === 0 ? (
                            <div className="p-12 text-center">
                                <Users className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                                <p className="text-nature-800/60 font-body">No subscribers yet</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-nature-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Source</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-nature-100">
                                        {newsletterSubs.map((sub, index) => (
                                            <tr key={index} className="hover:bg-nature-50/50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-nature-800">
                                                        <Mail className="w-4 h-4 text-nature-800/40" />
                                                        {sub.email}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        sub.source === 'lead_magnet' 
                                                            ? 'bg-copper-100 text-copper-800' 
                                                            : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {sub.source === 'lead_magnet' ? 'Free Download' : 'Newsletter'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2 text-sm text-nature-800">
                                                        <Calendar className="w-4 h-4 text-nature-800/40" />
                                                        {formatDate(sub.subscribed_at)}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Share Statistics */}
                {activeTab === 'shares' && (
                    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                        <div className="p-6 border-b border-nature-100">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <h2 className="font-heading text-xl text-nature-900">Share Statistics</h2>
                                
                                {/* Date Filter */}
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-nature-800/60 font-body">From:</label>
                                        <Input
                                            type="date"
                                            value={shareStartDate}
                                            onChange={(e) => setShareStartDate(e.target.value)}
                                            className="w-36"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm text-nature-800/60 font-body">To:</label>
                                        <Input
                                            type="date"
                                            value={shareEndDate}
                                            onChange={(e) => setShareEndDate(e.target.value)}
                                            className="w-36"
                                        />
                                    </div>
                                    <Button 
                                        onClick={fetchSharesWithFilter}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        Filter
                                    </Button>
                                    {(shareStartDate || shareEndDate) && (
                                        <Button 
                                            onClick={() => {
                                                setShareStartDate('');
                                                setShareEndDate('');
                                                const pwd = sessionStorage.getItem('adminAuth');
                                                if (pwd) {
                                                    axios.get(`${API}/admin/shares?password=${encodeURIComponent(pwd)}`)
                                                        .then(res => setShareStats(res.data));
                                                }
                                            }}
                                            variant="ghost"
                                            className="rounded-full text-sm"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                            </div>
                            
                            {/* Platform Totals Summary */}
                            {shareStats && shareStats.total_shares > 0 && (
                                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-nature-100">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-nature-50 rounded-full">
                                        <XIcon />
                                        <span className="text-sm font-body text-nature-800">{shareStats.platform_totals?.twitter || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-nature-50 rounded-full">
                                        <FacebookIcon />
                                        <span className="text-sm font-body text-nature-800">{shareStats.platform_totals?.facebook || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-nature-50 rounded-full">
                                        <LinkedInIcon />
                                        <span className="text-sm font-body text-nature-800">{shareStats.platform_totals?.linkedin || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-nature-50 rounded-full">
                                        <span className="text-sm font-body text-nature-800/60">Copy:</span>
                                        <span className="text-sm font-body text-nature-800">{shareStats.platform_totals?.copy || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-nature-50 rounded-full">
                                        <Smartphone className="w-4 h-4 text-nature-600" />
                                        <span className="text-sm font-body text-nature-800">{shareStats.platform_totals?.native || 0}</span>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-copper-100 rounded-full">
                                        <Share2 className="w-4 h-4 text-copper-600" />
                                        <span className="text-sm font-body font-medium text-copper-800">Total: {shareStats.total_shares}</span>
                                    </div>
                                    <div className="ml-auto">
                                        <Button 
                                            onClick={deleteShares}
                                            variant="outline"
                                            className="rounded-full text-red-500 hover:text-red-600 hover:border-red-300"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete {(shareStartDate || shareEndDate) ? 'Filtered' : 'All'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {!shareStats || shareStats.total_shares === 0 ? (
                            <div className="p-12 text-center">
                                <Share2 className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                                <p className="text-nature-800/60 font-body">No shares tracked yet</p>
                                <p className="text-nature-800/40 font-body text-sm mt-2">
                                    Shares will appear here when visitors use the Share button
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-nature-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Item</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-nature-800/60 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">
                                                <div className="flex items-center justify-center gap-1">
                                                    <XIcon />
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">
                                                <div className="flex items-center justify-center gap-1">
                                                    <FacebookIcon />
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">
                                                <div className="flex items-center justify-center gap-1">
                                                    <LinkedInIcon />
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">Copy</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Smartphone className="w-4 h-4" />
                                                </div>
                                            </th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-nature-800/60 uppercase tracking-wider">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-nature-100">
                                        {shareStats.shares.map((share, index) => (
                                            <tr key={index} className="hover:bg-nature-50/50">
                                                <td className="px-6 py-4">
                                                    <span className="text-sm text-nature-800 font-medium">
                                                        {share.slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        share.type === 'recording' 
                                                            ? 'bg-purple-100 text-purple-800' 
                                                            : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {share.type === 'recording' ? 'Recording' : 'Blog'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm text-nature-800">{share.twitter}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm text-nature-800">{share.facebook}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm text-nature-800">{share.linkedin}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm text-nature-800">{share.copy}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm text-nature-800">{share.native || 0}</span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="text-sm font-medium text-copper-600">{share.total}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Blog Posts */}
                {activeTab === 'blog' && (
                    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                        <div className="p-6 border-b border-nature-100 flex items-center justify-between">
                            <h2 className="font-heading text-xl text-nature-900">Blog Posts</h2>
                            <Button onClick={openNewPost} className="bg-copper-500 hover:bg-copper-600 rounded-full">
                                <Plus className="w-4 h-4 mr-2" />
                                New Post
                            </Button>
                        </div>
                        
                        {blogPosts.length === 0 ? (
                            <div className="p-12 text-center">
                                <FileText className="w-12 h-12 text-nature-800/30 mx-auto mb-4" />
                                <p className="text-nature-800/60 font-body mb-4">No blog posts yet</p>
                                <Button onClick={openNewPost} variant="outline" className="rounded-full">
                                    Create Your First Post
                                </Button>
                            </div>
                        ) : (
                            <div className="divide-y divide-nature-100">
                                {blogPosts.map((post) => (
                                    <div key={post.id} className="p-6 flex items-center justify-between hover:bg-nature-50/50">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="font-heading text-lg text-nature-900">{post.title}</h3>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    post.published 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {post.published ? 'Published' : 'Draft'}
                                                </span>
                                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {post.views || 0} views
                                                </span>
                                            </div>
                                            <p className="text-sm text-nature-800/60 font-body">{post.excerpt}</p>
                                            <p className="text-xs text-nature-800/40 font-body mt-1">
                                                {formatDate(post.created_at)} • /blog/{post.slug}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => openEditPost(post)}
                                                className="rounded-full"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                onClick={() => deleteBlogPost(post.slug)}
                                                className="rounded-full text-red-500 hover:text-red-600 hover:border-red-300"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Blog Editor Modal */}
            {showBlogEditor && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-nature-100 flex items-center justify-between">
                            <h2 className="font-heading text-xl text-nature-900">
                                {editingPost ? 'Edit Post' : 'New Post'}
                            </h2>
                            <button onClick={closeBlogEditor} className="text-nature-800/50 hover:text-nature-800">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-nature-800 mb-1">Title</label>
                                    <Input
                                        value={blogForm.title}
                                        onChange={(e) => setBlogForm({...blogForm, title: e.target.value})}
                                        placeholder="Enter post title"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-nature-800 mb-1">Excerpt</label>
                                    <Input
                                        value={blogForm.excerpt}
                                        onChange={(e) => setBlogForm({...blogForm, excerpt: e.target.value})}
                                        placeholder="Brief description for listing page"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-nature-800 mb-1">Content</label>
                                    <textarea
                                        value={blogForm.content}
                                        onChange={(e) => setBlogForm({...blogForm, content: e.target.value})}
                                        placeholder="Write your post content here...

Use ## for headings
Use ### for subheadings
Use - for bullet points"
                                        rows={15}
                                        className="w-full px-3 py-2 border border-nature-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-copper-500 font-body text-sm"
                                    />
                                </div>
                                
                                {/* SEO Fields */}
                                <div className="pt-4 border-t border-nature-200">
                                    <p className="text-sm font-medium text-nature-800 mb-3">SEO Settings (optional)</p>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-nature-800/60 mb-1">URL Slug</label>
                                            <Input
                                                value={blogForm.seo_slug}
                                                onChange={(e) => setBlogForm({...blogForm, seo_slug: e.target.value})}
                                                placeholder={blogForm.title ? blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : 'auto-generated-from-title'}
                                            />
                                            <p className="text-xs text-nature-800/40 mt-1">Leave empty to auto-generate from title</p>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs text-nature-800/60 mb-1">Title Tag</label>
                                            <Input
                                                value={blogForm.seo_title}
                                                onChange={(e) => setBlogForm({...blogForm, seo_title: e.target.value})}
                                                placeholder={blogForm.title || 'Falls back to post title'}
                                            />
                                            <p className="text-xs text-nature-800/40 mt-1">Leave empty to use post title</p>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs text-nature-800/60 mb-1">Meta Description</label>
                                            <textarea
                                                value={blogForm.seo_description}
                                                onChange={(e) => setBlogForm({...blogForm, seo_description: e.target.value})}
                                                placeholder={blogForm.excerpt || 'Falls back to excerpt'}
                                                rows={2}
                                                className="w-full px-3 py-2 border border-nature-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-copper-500 font-body text-sm"
                                            />
                                            <p className="text-xs text-nature-800/40 mt-1">Leave empty to use excerpt</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="published"
                                        checked={blogForm.published}
                                        onChange={(e) => setBlogForm({...blogForm, published: e.target.checked})}
                                        className="w-4 h-4 text-copper-500 rounded"
                                    />
                                    <label htmlFor="published" className="text-sm text-nature-800">Publish immediately</label>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-6 border-t border-nature-100 flex justify-end gap-3">
                            <Button variant="outline" onClick={closeBlogEditor} className="rounded-full">
                                Cancel
                            </Button>
                            <Button onClick={saveBlogPost} className="bg-copper-500 hover:bg-copper-600 rounded-full">
                                <Save className="w-4 h-4 mr-2" />
                                {editingPost ? 'Update Post' : 'Publish Post'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
